import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
  TextField,
  Alert,
} from '@mui/material';
import { Upload as UploadIcon, Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import { Template } from '../types/template';
import { templateApi, postcardApi } from '../services/api';
import { getFileUrl } from '../services/api';
import TokenDisplay from '../components/TokenDisplay';
import { useAuth } from '../hooks/useAuth';
import { userService } from '../services/userService';

export const CreatePostcard: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [referenceImagePreview, setReferenceImagePreview] = useState<string>('');
  const [referenceImageUri, setReferenceImageUri] = useState<string>('');
  const [generatedImageUri, setGeneratedImageUri] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [tokens, setTokens] = useState(0);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (templateId) {
      loadTemplate();
    }
  }, [templateId]);

  useEffect(() => {
    if (user) {
      userService.getProfile().then((user) => {
        setTokens(user.tokens);
      });
    }
  }, [user, generatedImageUri]);

  const loadTemplate = async () => {
    try {
      setIsLoading(true);
      const response = await templateApi.getTemplate(templateId!);
      setTemplate(response.data);
    } catch (error) {
      setError('Failed to load template');
      console.error('Error loading template:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReferenceImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setReferenceImage(file);
      setReferenceImagePreview(URL.createObjectURL(file));
    }
  };

  const handleGenerate = async () => {
    if (!referenceImage || !template) return;

    try {
      setIsLoading(true);
      setError('');

      const formData = new FormData();
      formData.append('referenceImage', referenceImage);
      formData.append('templateId', templateId!);

      const response = await postcardApi.generate(formData);
      setGeneratedImageUri(response.data.generatedImageUri);
      setReferenceImageUri(response.data.referenceImageUri);
    } catch (error: any) {
      if (error.response?.status === 402) {
        // Insufficient tokens
        navigate('/buy-tokens', { 
          state: { 
            returnTo: `/create/${templateId}`,
            message: 'You need more tokens to generate postcards. Please purchase a token package to continue.'
          }
        });
      } else {
        setError('Failed to generate image');
        console.error('Error generating image:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!generatedImageUri || !referenceImageUri || !template) return;

    try {
      setIsLoading(true);
      setError('');

      const postcardData = {
        templateId: templateId!,
        imageUrl: generatedImageUri,
        referenceImageUrl: referenceImageUri,
      };

      const response = await postcardApi.create(postcardData);
      navigate(`/postcards/${response.data.id}`);
    } catch (error) {
      setError('Failed to save postcard');
      console.error('Error saving postcard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !template) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!template) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">Template not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box py={4}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Create Postcard
          </Typography>
          <TokenDisplay tokens={tokens} />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Template: {template.title}
          </Typography>
          <Box
            component="img"
            src={getFileUrl(template.imageUrl)}
            alt={template.title}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: 400,
              objectFit: 'contain',
              mb: 2,
            }}
          />
          
          <Typography variant="body1" gutterBottom>
            {template.description}
          </Typography>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Your Reference Image
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="reference-image-upload"
              type="file"
              onChange={handleReferenceImageChange}
            />
            <label htmlFor="reference-image-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadIcon />}
                disabled={isLoading}
              >
                Upload Reference Image
              </Button>
            </label>
          </Box>

          {referenceImagePreview && (
            <Box
              component="img"
              src={referenceImagePreview}
              alt="Reference"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: 400,
                objectFit: 'contain',
                mb: 2,
              }}
            />
          )}

          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={!referenceImage || isLoading}
            startIcon={<RefreshIcon />}
            sx={{ mr: 1 }}
          >
            {generatedImageUri ? 'Regenerate' : 'Generate'}
          </Button>
        </Paper>

        {generatedImageUri && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Generated Postcard
            </Typography>
            
            <Box
              component="img"
              src={getFileUrl(generatedImageUri)}
              alt="Generated"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: 400,
                objectFit: 'contain',
                mb: 2,
              }}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isLoading}
              startIcon={<SaveIcon />}
            >
              Save Postcard
            </Button>
          </Paper>
        )}
      </Box>
    </Container>
  );
}; 

export default CreatePostcard;
